import React, { useState, useEffect, useRef, useMemo } from "react"
import PropTypes from "prop-types"

import Link from "~components/link/link"
import { ScrollingTooltips, TooltipContext } from "~components/scrolling-tooltips/scrolling-tooltips"
import { SideMenu, SideMenuItem, SideMenuContent, SideMenuTriggerLink } from "@fastly/consistently-vanilla"

import * as styles from "./sidebar.module.css"

export default ({ nodes }) => {
  const [showSidebar, setShowSidebar] = useState(false)
  const sideMenuRef = useRef(null)

  const activeNode = nodes.find((n) => n.isActive)
  return (
    <ScrollingTooltips>
      <TooltipContext.Consumer>
        {(setCurrentTooltip) => (
          <>
            <button
              className={styles.mobileSidebarToggle}
              aria-expanded={showSidebar ? true : false}
              aria-controls="sidebar"
              onClick={() => setShowSidebar((prevVal) => !prevVal)}
            >
              Navigate to...
            </button>
            <SideMenu
              data-swiftype-index="false"
              ref={sideMenuRef}
              defaultValue={[activeNode?.title]}
              onScroll={() => setCurrentTooltip(null)}
              display={{ _: showSidebar ? "block" : "none", md: "block" }}
            >
              <SideMenuItems nodes={nodes} sideMenuRef={sideMenuRef} />
            </SideMenu>
          </>
        )}
      </TooltipContext.Consumer>
    </ScrollingTooltips>
  )
}

const SideMenuItems = ({ nodes, sideMenuRef, level = 1 }) => {
  // We need this to ensure text is left-aligned when some sibilings have children and others don’t
  const siblingsHaveChildren = nodes.some((node) => node.children?.length && !node.hideChildren)

  return nodes.map((node) => {
    return (
      <TreeNode
        key={node.title}
        level={level}
        isCollapsed={!node.isActive}
        isActive={node.isActive}
        path={node.externalUrl || node.path}
        title={node.title}
        children={node.children}
        unlisted={node.unlisted}
        hideChildren={node.hideChildren}
        siblingsHaveChildren={siblingsHaveChildren}
        sideMenuRef={sideMenuRef}
      />
    )
  })
}

const TreeNode = ({
  level,
  isCollapsed,
  isActive,
  path,
  navTitle,
  title,
  children = [],
  unlisted,
  hideChildren,
  siblingsHaveChildren,
  sideMenuRef,
}) => {
  const nodeEl = useRef(null)
  const [tooltip, setTooltip] = useState(false)

  useEffect(() => {
    if (!nodeEl.current) return
    const hasActiveChildren = children.find((n) => n.isActive)

    if (isActive && !hasActiveChildren) {
      nodeEl.current.scrollIntoView({ block: "center", behavior: "instant" })
      window.scrollTo({ top: 0, left: 0, behavior: "instant" })
    }
    const sideMenuWidth = sideMenuRef?.current?.getBoundingClientRect()?.width ?? 320
    setTooltip(nodeEl.current.firstChild.getBoundingClientRect().right > sideMenuWidth)
  }, [])

  if (
    (unlisted || (children && children.length && !children.some(({ unlisted }) => !unlisted))) &&
    !(isActive && children.length)
  )
    return null

  const activateTooltip = (setCurrentTooltip) => {
    if (!tooltip) return

    setCurrentTooltip({
      position: nodeEl.current.firstChild.getBoundingClientRect(),
      link: path,
      title,
    })
  }

  const showRevealers = !hideChildren && !!children.length
  // Default padding is 8, but we want to indent if sibling nodes have children
  const paddingLeft = !showRevealers && siblingsHaveChildren ? 24 : 8

  return (
    <TooltipContext.Consumer>
      {(setCurrentTooltip) => (
        <SideMenuItem
          key={title}
          value={title}
          onMouseLeave={() => setCurrentTooltip(null)}
          ref={nodeEl}
          style={{ scrollMarginTop: "18px" }}
        >
          <SideMenuTriggerLink
            as={Link}
            to={path}
            style={{ marginLeft: level * 16, paddingLeft }}
            onMouseEnter={() => {
              activateTooltip(setCurrentTooltip)
            }}
            showRevealers={showRevealers}
          >
            {navTitle || title}
          </SideMenuTriggerLink>
          {!hideChildren && !isCollapsed && !!children.length ? (
            <SideMenuContent type="multiple" defaultValue={[children.find((n) => n.isActive)?.title]}>
              {children ? <SideMenuItems sideMenuRef={sideMenuRef} nodes={children} level={level + 1} /> : null}
            </SideMenuContent>
          ) : null}
        </SideMenuItem>
      )}
    </TooltipContext.Consumer>
  )
}
const NodeType = {
  level: PropTypes.number,
  isCollapsed: PropTypes.bool,
  isActive: PropTypes.bool,
  path: PropTypes.string,
  title: PropTypes.string,
}
NodeType.children = PropTypes.arrayOf(PropTypes.shape(NodeType))

TreeNode.propTypes = NodeType
